import React from 'react';
import {
  Grid,
  Box,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import { Nullable } from '@jamesgmarks/utilities';
import { HHDatePicker } from 'src/components/parts/HHDatePicker';
import { dateFromYmd, getNumDaysBetween, makeYmd } from 'src/app-utils';
import { ISubscriptionData } from '../types';

interface ISubscriptionDatesProps {
  subscriptionData: ISubscriptionData;
  setSubscriptionData: React.Dispatch<React.SetStateAction<ISubscriptionData>>;
  getDefaultRateExpiryDateFromSubscriptionStartDate: (d: Nullable<string>) => Nullable<Date>;
}

const calculateTimeSpan = (startDate: string | null, expiryDate: string | null) => {
  if (!startDate || !expiryDate) {
    return { totalDays: null, years: 0, months: 0, days: 0, isInfinite: !expiryDate };
  }

  const start = new Date(startDate);
  const end = new Date(expiryDate);
  const totalDays = getNumDaysBetween(start, end);

  if (totalDays < 0) {
    return { totalDays, years: 0, months: 0, days: 0, isInfinite: false };
  }

  // Calculate years, months, and days
  let years = 0;
  let months = 0;
  let days = totalDays;

  if (days >= 365) {
    years = Math.floor(days / 365);
    days = days % 365;
  }

  if (days >= 30) {
    months = Math.floor(days / 30);
    days = days % 30;
  }

  return { totalDays, years, months, days, isInfinite: false };
};

export const SubscriptionDates: React.FC<ISubscriptionDatesProps> = ({
  subscriptionData,
  setSubscriptionData,
  getDefaultRateExpiryDateFromSubscriptionStartDate,
}) => {
  const timeSpan = calculateTimeSpan(subscriptionData.startDate, subscriptionData.expiryDate);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <HHDatePicker
          label="Start date"
          value={subscriptionData.startDate ? dateFromYmd(subscriptionData.startDate) : null}
          onChange={(date: Nullable<Date>) => {
            setSubscriptionData({
              ...subscriptionData,
              startDate: date ? makeYmd(date, true) : makeYmd(new Date(), true),
            });
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <HHDatePicker
          label="Expiry date"
          value={subscriptionData.expiryDate ? dateFromYmd(subscriptionData.expiryDate) : null}
          onChange={(date: Nullable<Date>) => {
            setSubscriptionData({
              ...subscriptionData,
              expiryDate: date ? makeYmd(date, true) : null,
            });
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Box>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              minHeight: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            {timeSpan.isInfinite ? (
              <Chip
                label="∞ Indefinite"
                variant="outlined"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
            ) : timeSpan.totalDays === null ? (
              <Typography variant="body2" color="text.secondary">
                Set dates to see duration
              </Typography>
            ) : timeSpan.totalDays < 0 ? (
              <Chip
                label="Invalid dates"
                variant="outlined"
                color="error"
                size="small"
              />
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {timeSpan.totalDays === 0 ? (
                  <Chip label="Same day" variant="outlined" size="small" />
                ) : (
                  <>
                    {timeSpan.years > 0 && (
                      <Chip
                        label={`${timeSpan.years} year${timeSpan.years !== 1 ? 's' : ''}`}
                        variant="outlined"
                        size="small"
                        color="primary"
                      />
                    )}
                    {timeSpan.months > 0 && (
                      <Chip
                        label={`${timeSpan.months} month${timeSpan.months !== 1 ? 's' : ''}`}
                        variant="outlined"
                        size="small"
                        color="secondary"
                      />
                    )}
                    {timeSpan.days > 0 && (
                      <Chip
                        label={`${timeSpan.days} day${timeSpan.days !== 1 ? 's' : ''}`}
                        variant="outlined"
                        size="small"
                      />
                    )}
                    <Box sx={{ ml: 1, alignSelf: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        ({timeSpan.totalDays} total days)
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <HHDatePicker
          label="Rate expiry date"
          value={
            subscriptionData.rateExpiryDate
              ? (subscriptionData.rateExpiryDate === '0000-00-00' ? null : dateFromYmd(subscriptionData.rateExpiryDate))
              : null
          }
          onChange={(date: Nullable<Date>) => {
            setSubscriptionData((old) => ({
              ...old,
              rateExpiryDate: (
                (date === null || isNaN(date.getTime())) ? null : makeYmd(date, true)
              ),
            }));
          }}
        />
      </Grid>
    </Grid>
  );
};
